import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { supportApi, ApiSupportTicket, ApiTicketMessage } from '../lib/api';
import { showToast } from '../lib/toast';
import ImageLightbox from './ImageLightbox';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'bg-amber-50 text-amber-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  resolved: { label: 'Resolved', color: 'bg-emerald-50 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-500' },
};

const TOPIC_LABELS: Record<string, string> = {
  bug_report: 'Bug Report',
  feature_request: 'Feature Request',
  account_issue: 'Account Issue',
  notifications: 'Notifications',
  feedback: 'Feedback',
  other: 'Other',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const MAX_ATTACHMENTS = 3;

export default function TicketThread() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const ticketId = parseInt(id || '', 10);

  const [ticket, setTicket] = useState<ApiSupportTicket | null>(null);
  const [messages, setMessages] = useState<ApiTicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isNaN(ticketId)) return;
    supportApi
      .getTicket(ticketId)
      .then((res) => {
        setTicket(res.ticket);
        setMessages(res.messages);
      })
      .catch(() => {
        showToast('Failed to load ticket', 'error');
        navigate(-1);
      })
      .finally(() => setLoading(false));
  }, [ticketId, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAddPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const toAdd = files.slice(0, remaining);
    setAttachments((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews((prev) => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const msg = await supportApi.addMessage(
        ticketId,
        newMessage.trim(),
        attachments.length > 0 ? attachments : undefined,
      );
      setMessages((prev) => [...prev, msg]);
      setNewMessage('');
      setAttachments([]);
      setPreviews([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch {
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full flex-col bg-[#f8f5f5] items-center justify-center">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!ticket) return null;

  const status = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;

  return (
    <div className="fixed inset-0 w-full flex flex-col bg-[#f8f5f5] overflow-hidden overscroll-none">
      {/* Header */}
      <div className="flex items-center px-4 pb-3 pt-[calc(env(safe-area-inset-top)+1rem)] justify-between shrink-0 bg-[#f8f5f5] z-10 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <div className="text-center">
          <h1 className="text-sm font-bold text-slate-900">
            {TOPIC_LABELS[ticket.topic] || ticket.topic}
          </h1>
          <span
            className={cn(
              'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mt-1 inline-block',
              status.color,
            )}
          >
            {status.label}
          </span>
        </div>
        <div className="size-10" />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
        {/* Original ticket message */}
        <div className="flex justify-end">
          <div className="max-w-[80%]">
            <div className="bg-[#fa8585] text-white rounded-2xl rounded-br-md px-4 py-3">
              <p className="text-sm whitespace-pre-wrap">{ticket.message}</p>
            </div>
            {ticket.attachments.length > 0 && (
              <div className="flex gap-2 mt-1 justify-end">
                {ticket.attachments.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => setLightboxUrl(url)}
                    className="h-24 max-w-40 rounded-xl overflow-hidden border border-slate-200"
                  >
                    <img src={url} alt="" className="h-full w-auto object-contain" />
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-1 text-right">
              {formatTime(ticket.createdAt)}
            </p>
          </div>
        </div>

        {/* Thread messages */}
        {messages.map((msg) => {
          const isUser = msg.senderType === 'user';
          return (
            <div key={msg.id} className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
              <div className="max-w-[80%]">
                {!isUser && (
                  <p className="text-[10px] font-semibold text-slate-400 mb-1 ml-1">
                    {msg.senderName || 'Support'}
                  </p>
                )}
                {msg.message.trim() && (
                  <div
                    className={cn(
                      'rounded-2xl px-4 py-3',
                      isUser
                        ? 'bg-[#fa8585] text-white rounded-br-md'
                        : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md',
                    )}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                )}
                {msg.attachments.length > 0 && (
                  <div className={cn('flex gap-2 mt-1', isUser ? 'justify-end' : 'justify-start')}>
                    {msg.attachments.map((url, idx) => (
                      <button
                        key={idx}
                        onClick={() => setLightboxUrl(url)}
                        className="h-24 max-w-40 rounded-xl overflow-hidden border border-slate-200"
                      >
                        <img src={url} alt="" className="h-full w-auto object-contain" />
                      </button>
                    ))}
                  </div>
                )}
                <p
                  className={cn(
                    'text-[10px] text-slate-400 mt-1',
                    isUser ? 'text-right' : 'text-left',
                  )}
                >
                  {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Attachment previews */}
      {previews.length > 0 && (
        <div className="px-4 pb-2 flex gap-2 shrink-0">
          {previews.map((src, idx) => (
            <div
              key={idx}
              className="relative size-16 rounded-xl overflow-hidden border border-slate-200"
            >
              <img src={src} alt="" className="size-full object-cover" />
              <button
                onClick={() => removeAttachment(idx)}
                className="absolute top-0.5 right-0.5 size-5 bg-black/60 rounded-full flex items-center justify-center"
              >
                <X size={10} className="text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="shrink-0 bg-[#f8f5f5]/90 backdrop-blur-md border-t border-slate-200 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] flex items-end gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={attachments.length >= MAX_ATTACHMENTS}
          className="size-10 rounded-full flex items-center justify-center text-slate-400 hover:text-[#fa8585] hover:bg-slate-200 transition-colors disabled:opacity-30 shrink-0"
        >
          <ImageIcon size={20} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleAddPhoto}
        />
        <textarea
          ref={textareaRef}
          value={newMessage}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          style={{ minHeight: '44px' }}
          className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all resize-none max-h-32"
        />
        <button
          onClick={handleSend}
          disabled={sending || (!newMessage.trim() && attachments.length === 0)}
          className="size-10 rounded-full bg-[#fa8585] text-white flex items-center justify-center disabled:opacity-40 transition-all active:scale-95 shrink-0"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>

      {/* Lightbox */}
      {lightboxUrl && <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
